import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { FieldState, FormState } from 'formstate';

import { Api } from '@core/api';
import { useModel, wrapInFormField } from '@core/forms';
import { injectTSDI } from '@core/tsdi';
import { links } from '@core/router';
import { toast } from '@core/toast';
import { extractApiErrorMessage } from '@core/utils/errors';
import { useImageUpload } from '@components/hooks/use-image-upload';
import {
    ImageType,
    TeamAccess,
    TeamCreateSupportRequest,
    TeamLeaderboardVisibility,
    TeamMemberVisibility
} from '@api/ChallengeClient';

const DESCRIPTION_MAX = 500;
const NAME_MAX = 60;
export const MAX_TEAMS_PER_SUBMISSION = 100;

export type RowStatus = 'pending' | 'submitting' | 'success' | 'error';

export interface Row {
    id: string;
    name: string;
    status: RowStatus;
    errorMessage?: string;
}

export function useBulkCreateTeamsStore(communityId: number) {
    const { challengeClient } = injectTSDI(Api);
    const navigate = useNavigate();

    const model = useModel(() => {
        const form = new FormState({
            description: new FieldState<string>('').validators(
                (v) => (v.trim() ? false : 'Description is required'),
                (v) =>
                    v.trim().length > DESCRIPTION_MAX
                        ? `Maximum ${DESCRIPTION_MAX} characters`
                        : false
            ),
            image: new FormState({
                bucketName: new FieldState<string>('').validators((v) =>
                    v ? false : 'Image is required'
                ),
                objectKey: new FieldState<string>('').validators((v) =>
                    v ? false : 'Image is required'
                )
            }),
            access: new FieldState<TeamAccess>(TeamAccess.OPEN),
            memberVisibility: new FieldState<TeamMemberVisibility>(
                TeamMemberVisibility.FULL
            ),
            leaderboardVisibility: new FieldState<TeamLeaderboardVisibility>(
                TeamLeaderboardVisibility.FULL
            ),
            managerUserId: new FieldState<string>('').validators((v) =>
                v.trim() ? false : 'Manager user ID is required'
            ),
            memberLimitInput: new FieldState<string>('').validators((v) =>
                v && !/^[1-9]\d*$/.test(v)
                    ? 'Must be a positive integer'
                    : false
            )
        });

        return {
            form,
            fields: {
                description: wrapInFormField(form.$.description),
                image: {
                    bucketName: wrapInFormField(form.$.image.$.bucketName),
                    objectKey: wrapInFormField(form.$.image.$.objectKey)
                },
                access: wrapInFormField(form.$.access),
                memberVisibility: wrapInFormField(form.$.memberVisibility),
                leaderboardVisibility: wrapInFormField(
                    form.$.leaderboardVisibility
                ),
                managerUserId: wrapInFormField(form.$.managerUserId),
                memberLimitInput: wrapInFormField(form.$.memberLimitInput)
            }
        };
    });

    const imageUpload = useImageUpload(ImageType.TEAM, (cdn) => {
        model.fields.image.bucketName.onChange(cdn.bucketName);
        model.fields.image.objectKey.onChange(cdn.objectKey);
    });

    const nextIdRef = useRef(0);
    const newRow = (): Row => ({
        id: String(++nextIdRef.current),
        name: '',
        status: 'pending'
    });

    const [rows, setRows] = useState<Row[]>(() => [
        {
            id: String(++nextIdRef.current),
            name: '',
            status: 'pending'
        }
    ]);
    // Mirror of `rows` always reflecting the latest state — `onSubmit`
    // reads this rather than the closure-captured `rows` so it sees any
    // edits made after the render that produced the active onSubmit.
    const rowsRef = useRef<Row[]>(rows);
    useEffect(() => {
        rowsRef.current = rows;
    }, [rows]);

    const [submitting, setSubmitting] = useState(false);
    const [progress, setProgress] = useState<{
        current: number;
        total: number;
    } | null>(null);

    // Synchronous re-entry guard. setSubmitting is async and would let two
    // same-tick clicks both pass an "if (submitting) return" check.
    const isSubmittingRef = useRef(false);
    const abortRef = useRef<AbortController | null>(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        return () => {
            mountedRef.current = false;
            abortRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        if (!submitting) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [submitting]);

    const safeSetRows = (updater: (r: Row[]) => Row[]) => {
        if (mountedRef.current) {
            setRows(updater);
        }
    };

    const markStart = (id: string) =>
        safeSetRows((rs) =>
            rs.map((r) =>
                r.id === id
                    ? { ...r, status: 'submitting', errorMessage: undefined }
                    : r
            )
        );
    const markSuccess = (id: string) =>
        safeSetRows((rs) =>
            rs.map((r) =>
                r.id === id
                    ? { ...r, status: 'success', errorMessage: undefined }
                    : r
            )
        );
    const markError = (id: string, msg: string) =>
        safeSetRows((rs) =>
            rs.map((r) =>
                r.id === id ? { ...r, status: 'error', errorMessage: msg } : r
            )
        );

    const setRowName = (id: string, name: string) =>
        safeSetRows((rs) =>
            rs.map((r) => {
                if (r.id !== id) return r;
                // Don't reset success/submitting rows; their name is "locked in".
                if (r.status === 'success' || r.status === 'submitting')
                    return r;
                return {
                    ...r,
                    name,
                    status: 'pending',
                    errorMessage: undefined
                };
            })
        );

    const addRow = () =>
        safeSetRows((rs) =>
            rs.length >= MAX_TEAMS_PER_SUBMISSION ? rs : [...rs, newRow()]
        );

    const removeRow = (id: string) =>
        safeSetRows((rs) =>
            rs.length > 1 ? rs.filter((r) => r.id !== id) : rs
        );

    const onSubmit = async () => {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;

        try {
            const sharedResult = await model.form.validate();
            if (sharedResult.hasError) return;

            // Skip 'success' rows on retry. rowsRef sees edits committed after render.
            const latestRows = rowsRef.current;
            const candidates = latestRows
                .filter((r) => r.status === 'pending' || r.status === 'error')
                .map((r) => ({ id: r.id, name: r.name.trim() }));
            if (candidates.length === 0) return;

            // Case-insensitive comparisons match the backend's unique constraint.
            const successNamesLower = new Set(
                latestRows
                    .filter((r) => r.status === 'success')
                    .map((r) => r.name.toLowerCase())
            );
            const seen = new Map<string, string>();
            const errorsToApply: { id: string; msg: string }[] = [];

            for (const c of candidates) {
                if (!c.name) {
                    errorsToApply.push({ id: c.id, msg: 'Required' });
                    continue;
                }
                if (c.name.length > NAME_MAX) {
                    errorsToApply.push({
                        id: c.id,
                        msg: `Maximum ${NAME_MAX} characters`
                    });
                    continue;
                }
                const key = c.name.toLowerCase();
                if (seen.has(key)) {
                    errorsToApply.push({
                        id: c.id,
                        msg: 'Duplicate name in submission'
                    });
                    continue;
                }
                if (successNamesLower.has(key)) {
                    errorsToApply.push({
                        id: c.id,
                        msg: 'Duplicate of an already-created team'
                    });
                    continue;
                }
                seen.set(key, c.id);
            }

            if (errorsToApply.length > 0) {
                const errMap = new Map(errorsToApply.map((e) => [e.id, e.msg]));
                safeSetRows((rs) =>
                    rs.map((r) =>
                        errMap.has(r.id)
                            ? {
                                  ...r,
                                  status: 'error',
                                  errorMessage: errMap.get(r.id)
                              }
                            : r
                    )
                );
                return;
            }

            // signal is forwarded to fetch; aborting cancels the in-flight POST
            // so no orphan team is created server-side.
            abortRef.current = new AbortController();
            const signal = abortRef.current.signal;
            setSubmitting(true);
            setProgress({ current: 0, total: candidates.length });

            const sharedData: Omit<TeamCreateSupportRequest, 'name'> = {
                description: model.fields.description.value.trim(),
                image: {
                    bucketName: model.fields.image.bucketName.value,
                    objectKey: model.fields.image.objectKey.value
                },
                access: model.fields.access.value,
                memberVisibility: model.fields.memberVisibility.value,
                leaderboardVisibility: model.fields.leaderboardVisibility.value,
                managerUserId: model.fields.managerUserId.value.trim(),
                ...(model.fields.memberLimitInput.value.trim()
                    ? {
                          memberLimit: Number(
                              model.fields.memberLimitInput.value.trim()
                          )
                      }
                    : {})
            };

            let successCount = 0;
            let failCount = 0;
            let stopped = false;

            try {
                for (let i = 0; i < candidates.length; i++) {
                    if (signal.aborted) {
                        stopped = true;
                        break;
                    }
                    const c = candidates[i];
                    setProgress({
                        current: i + 1,
                        total: candidates.length
                    });
                    markStart(c.id);

                    try {
                        await challengeClient.support.teamSupportControllerCreateTeam(
                            communityId,
                            { ...sharedData, name: c.name },
                            { signal }
                        );
                        markSuccess(c.id);
                        successCount++;
                    } catch (err) {
                        if (signal.aborted) {
                            stopped = true;
                            break;
                        }
                        markError(
                            c.id,
                            extractApiErrorMessage(err, 'Failed to create team')
                        );
                        failCount++;
                    }
                }
            } finally {
                // Reset any 'submitting' rows that were interrupted (abort).
                safeSetRows((rs) =>
                    rs.map((r) =>
                        r.status === 'submitting'
                            ? {
                                  ...r,
                                  status: 'pending',
                                  errorMessage: undefined
                              }
                            : r
                    )
                );
                setSubmitting(false);
                setProgress(null);
                abortRef.current = null;
            }

            if (!stopped) {
                if (failCount === 0 && successCount > 0) {
                    toast.success(
                        `${successCount} team${
                            successCount === 1 ? '' : 's'
                        } created`
                    );
                    navigate(links.communityTeams(String(communityId)));
                } else if (successCount === 0) {
                    toast.error(`No teams were created. ${failCount} failed.`);
                } else {
                    toast.warning(
                        `${successCount} created, ${failCount} failed`
                    );
                }
            }
        } finally {
            isSubmittingRef.current = false;
        }
    };

    const onCancel = () => {
        // Check abortRef directly rather than the `submitting` state — there's
        // a tiny window where the inner finally has already cleared submitting
        // but the outer finally has not yet released isSubmittingRef.
        if (abortRef.current) {
            abortRef.current.abort();
            return;
        }
        navigate(links.communityTeams(String(communityId)));
    };

    return {
        fields: model.fields,
        rows,
        addRow,
        removeRow,
        setRowName,
        onSubmit,
        onCancel,
        submitting,
        progress,
        imageUpload
    };
}
